import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "../components/app/AppLayout";
import { companiesApi, facilitiesApi, auth } from "../lib/api";
import {
  Building2, Upload, Loader2, Search, Download, ChevronRight, Users, X, Plus, Save,
  Filter, ShieldAlert, FileSpreadsheet, Trash2, ArrowUpDown, ArrowUp, ArrowDown,
  FileText,
} from "lucide-react";
import { getApiError } from "../lib/utils";

// ---------------------------------------------------------------------------
// Overrides editor — searchable multi-select shared for "extras" and "exclusions"
// ---------------------------------------------------------------------------
const FacilityPicker = ({ label, ids, setIds, tone = "green" }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [details, setDetails] = useState({});
  const [busy, setBusy] = useState(false);
  const chipClass = tone === "red"
    ? "bg-red-50 border-red-200 text-red-800"
    : "bg-emerald-50 border-emerald-200 text-emerald-800";

  useEffect(() => {
    const missing = ids.filter((id) => !details[id]);
    if (!missing.length) return;
    Promise.all(missing.map((id) => facilitiesApi.get(id).catch(() => null))).then((rows) => {
      setDetails((prev) => {
        const next = { ...prev };
        rows.forEach((r) => { if (r) next[r.facility_id] = r; });
        return next;
      });
    });
  }, [ids, details]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const d = await facilitiesApi.list({ q: q.trim(), page: 1, page_size: 6 });
        if (!cancelled) setResults(d.items || []);
      } finally { if (!cancelled) setBusy(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const add = (f) => { if (!ids.includes(f.facility_id)) { setIds([...ids, f.facility_id]); setDetails((d) => ({ ...d, [f.facility_id]: f })); setQ(""); setResults([]); } };
  const remove = (id) => setIds(ids.filter((x) => x !== id));

  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="relative mb-2">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search facility to add…"
          className="w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 py-1.5 text-xs outline-none focus:border-nmi-600"
        />
        {busy && <Loader2 size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      {results.length > 0 && (
        <div className="mb-2 max-h-40 overflow-y-auto rounded-md border border-slate-200">
          {results.map((r) => (
            <button
              key={r.facility_id}
              data-testid={`picker-add-${r.facility_id}`}
              onClick={() => add(r)}
              className="flex w-full items-start justify-between gap-2 border-b border-slate-100 px-2 py-1.5 text-left last:border-0 hover:bg-slate-50"
              disabled={ids.includes(r.facility_id)}
            >
              <div className="text-xs">
                <div className="font-semibold text-slate-900">{r.name}</div>
                <div className="text-[10px] text-slate-500">{r.category} · {r.town || "—"}, {r.region || "—"}</div>
              </div>
              <Plus size={12} className="mt-1 text-nmi-700" />
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {ids.length === 0 && <div className="text-[11px] italic text-slate-400">None yet.</div>}
        {ids.map((id) => (
          <span key={id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${chipClass}`}>
            {details[id]?.name || id}
            <button onClick={() => remove(id)} className="rounded-full hover:bg-black/10 p-0.5"><X size={10} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reusable info row (label + value) for the drawer
// ---------------------------------------------------------------------------
const InfoCell = ({ label, value, mono = false, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className={`mt-0.5 break-words text-xs text-slate-800 ${mono ? "font-mono" : ""}`}>
      {value === null || value === undefined || value === "" ? <span className="italic text-slate-400">—</span> : value}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Company detail drawer — surfaces EVERY field from the upload
// ---------------------------------------------------------------------------
const CompanyDrawer = ({ companyId, onClose, onChanged }) => {
  const user = auth.me();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const [data, setData] = useState(null);
  const [extras, setExtras] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const downloadReport = async () => {
    try {
      const blob = await companiesApi.reportPdfBlob(companyId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `NMI_${(data?.full?.name || companyId).replace(/[^\w-]+/g, "_")}_facilities.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Report download failed"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const d = await companiesApi.facilities(companyId, { page: 1, page_size: 25 });
      const full = await companiesApi.get(companyId);
      setData({ ...d, full });
      setExtras(d.company.extra_facility_ids || []);
      setExcluded(d.company.excluded_facility_ids || []);
    } catch { toast.error("Failed to load"); onClose(); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const save = async () => {
    setSaving(true);
    try {
      await companiesApi.setOverrides(companyId, extras, excluded);
      toast.success("Overrides saved");
      await load(); onChanged?.();
    } catch (e) { toast.error(getApiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const c = data?.full;
  const membersByPlan = c?.members_by_plan || {};

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="ml-auto flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 p-5">
          {loading || !data ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : (
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">Company</div>
              <h2 className="font-heading text-xl font-black tracking-tight text-slate-900" data-testid="company-drawer-name">{c.name}</h2>
              <div className="mt-0.5 font-mono text-[11px] text-slate-500">{c.company_id}</div>
              <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                {(c.plans || []).map((p) => (
                  <span key={p} className="rounded bg-nmi-100 px-1.5 py-0.5 font-mono font-bold uppercase tracking-wider text-nmi-800">
                    {p}{membersByPlan[p] ? ` · ${membersByPlan[p].toLocaleString()}` : ""}
                  </span>
                ))}
                {(c.plans || []).length === 0 && <span className="italic text-slate-400">No plans on record</span>}
              </div>
            </div>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={downloadReport}
              disabled={loading || !data}
              data-testid="company-download-report"
              className="inline-flex items-center gap-1.5 rounded-md border border-nmi-600 bg-white px-3 py-1.5 text-xs font-semibold text-nmi-700 hover:bg-nmi-50 disabled:opacity-50"
              title="Download the accessible-facilities report as PDF"
            >
              <FileText size={13} strokeWidth={2.5} /> PDF
            </button>
            <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        {c && (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">

            {/* Membership + status */}
            <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4 sm:grid-cols-3" data-testid="company-membership-block">
              <InfoCell label="Total members" value={c.total_members?.toLocaleString?.() || c.total_members} mono />
              <InfoCell label="Status" value={c.status} />
              <InfoCell label="Accessible facilities" value={data.total.toLocaleString()} mono />
              <InfoCell label="Start date" value={c.start_date} mono />
              <InfoCell label="End date" value={c.end_date} mono />
              <InfoCell label="Join date" value={c.join_date} mono />
            </div>

            {/* Members-by-plan breakdown */}
            {Object.keys(membersByPlan).length > 0 && (
              <div className="rounded-md border border-slate-200 p-4">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Members by plan</div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {Object.entries(membersByPlan).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
                    <div key={p} className="rounded-md border border-nmi-200 bg-nmi-50 p-2 text-xs">
                      <div className="font-mono font-bold uppercase tracking-wider text-nmi-800">{p}</div>
                      <div className="mt-0.5 text-slate-700">{n.toLocaleString()} members</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact & staff */}
            <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
              <InfoCell label="Sales officer" value={c.sales_officer} />
              <InfoCell label="Relationship officer" value={c.relationship_officer} />
              <InfoCell label="Contact person" value={c.contact_person} />
              <InfoCell label="Telephone" value={c.telephone} mono />
              <InfoCell label="Email" value={c.email} mono className="col-span-2" />
              <InfoCell label="Postal address" value={c.postal_address} className="col-span-2" />
              <InfoCell label="Nature of business" value={c.nature_of_business} className="col-span-2" />
            </div>

            {/* Extra fields captured but not standard */}
            {(() => {
              const known = new Set([
                "_id", "company_id", "name", "plans", "members_by_plan", "total_members",
                "start_date", "end_date", "join_date", "status", "sales_officer",
                "relationship_officer", "contact_person", "telephone", "email",
                "postal_address", "nature_of_business", "extra_facility_ids",
                "excluded_facility_ids", "imported_at", "imported_by",
                "overrides_updated_at", "overrides_updated_by",
                "missing_from_last_import",
              ]);
              const extraKeys = Object.keys(c).filter((k) => !known.has(k));
              if (extraKeys.length === 0) return null;
              return (
                <div className="rounded-md border border-slate-200 p-4">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Other columns from your upload
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {extraKeys.map((k) => (
                      <InfoCell key={k} label={k} value={String(c[k])} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Overrides */}
            {isAdmin && (
              <div className="space-y-4 rounded-md border border-slate-200 p-4">
                <div>
                  <div className="font-heading text-sm font-bold text-slate-900">Access overrides</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    Additions grant access to facilities the plan wouldn't normally cover. Exclusions remove access even if the plan qualifies.
                  </div>
                </div>
                <FacilityPicker label="Extra access (add facilities)" ids={extras} setIds={setExtras} tone="green" />
                <FacilityPicker label="Excluded facilities (opt-out)" ids={excluded} setIds={setExcluded} tone="red" />
                <div className="flex justify-end">
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid="company-save-overrides"
                    className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-4 py-2 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={2.5} />}
                    Save overrides
                  </button>
                </div>
              </div>
            )}

            {/* Facilities preview */}
            <div className="rounded-md border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                First {data.items.length} of {data.total} accessible facilities
              </div>
              <div className="divide-y divide-slate-100">
                {data.items.map((f) => (
                  <div key={f.facility_id} className="flex items-start justify-between gap-2 px-4 py-2 text-xs">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-900">{f.name}</div>
                      <div className="text-[11px] text-slate-500">{f.category} · {f.town || "—"}, {f.region || "—"}</div>
                    </div>
                    <div className="shrink-0 text-[10px] font-mono text-slate-500">{f.nmi_plans || "—"}</div>
                  </div>
                ))}
                {data.items.length === 0 && <div className="p-6 text-center text-xs text-slate-500">No facilities accessible under this company's plans.</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mass replacement modal for companies
// ---------------------------------------------------------------------------
const MassReplaceModal = ({ onClose, onDone }) => {
  const replaceRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [mode, setMode] = useState("cross_reference");
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [executing, setExecuting] = useState(false);

  const runPreview = async () => {
    if (!file) return;
    setPreviewing(true); setPreview(null);
    try { setPreview(await companiesApi.previewReplace(file)); }
    catch (e) { toast.error(getApiError(e, "Preview failed")); }
    finally { setPreviewing(false); }
  };

  const runReplace = async () => {
    if (!file || confirmPhrase !== "RESET") return;
    setExecuting(true);
    try {
      const r = await companiesApi.executeReplace(file, {
        mode, confirm: "RESET",
        deleteMissing: mode === "cross_reference" ? deleteMissing : false,
      });
      toast.success(`Companies replaced — ${r.added} added, ${r.updated} updated`);
      onDone?.(r);
      onClose();
    } catch (e) { toast.error(getApiError(e, "Replacement failed")); setConfirmPhrase(""); }
    finally { setExecuting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="mx-auto my-8 w-full max-w-3xl rounded-lg border-2 border-red-300 bg-white shadow-2xl" data-testid="companies-mass-replace-modal">
        <div className="flex items-start justify-between border-b border-slate-200 p-5">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
              <ShieldAlert size={20} className="text-red-700" />
            </div>
            <div>
              <div className="font-heading text-lg font-bold text-slate-900">Mass replace companies</div>
              <p className="mt-1 text-xs text-slate-600">
                Upload a new companies workbook. A backup snapshot is saved automatically before any change. Existing
                access overrides (extras / exclusions) are preserved when a company survives.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="space-y-4 p-5">
          <div
            onClick={() => replaceRef.current?.click()}
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-red-300 bg-red-50/40 p-8 text-center hover:bg-red-50/60"
          >
            <input
              ref={replaceRef}
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              data-testid="companies-mass-replace-file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            />
            <FileSpreadsheet size={22} className="text-red-700" />
            <div className="mt-2 font-heading text-sm font-bold text-slate-900">
              {file ? file.name : "Drop cleaned companies workbook (.xlsx)"}
            </div>
            <div className="mt-1 text-[11px] text-slate-500">Uses the same template as regular upload.</div>
          </div>

          <div className="flex justify-end gap-2">
            {file && <button onClick={() => { setFile(null); setPreview(null); }} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Clear</button>}
            <button
              onClick={runPreview}
              disabled={!file || previewing}
              data-testid="companies-mass-replace-preview"
              className="inline-flex items-center gap-2 rounded-md border border-red-600 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} strokeWidth={2.5} />}
              Preview diff
            </button>
          </div>

          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <PreviewStat label="In file" value={preview.counts.new_file_rows} />
                <PreviewStat label="In DB" value={preview.counts.existing_db_rows} />
                <PreviewStat label="Will add" value={preview.counts.adds} tone="good" />
                <PreviewStat label="Will update" value={preview.counts.updates} tone="info" />
                <PreviewStat label="Not in file" value={preview.counts.removes} tone="warn" />
              </div>

              <details className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
                <summary className="cursor-pointer font-semibold">Existing companies missing from new file ({preview.counts.removes})</summary>
                <ul className="mt-2 max-h-56 overflow-y-auto list-disc pl-4">
                  {preview.removes.map((r) => (
                    <li key={r.company_id}><b>{r.name}</b> — {r.plans?.join(", ") || "—"} · {r.status || "—"}</li>
                  ))}
                  {preview.counts.removes > preview.removes.length && (
                    <li className="italic text-slate-500">…and {preview.counts.removes - preview.removes.length} more</li>
                  )}
                </ul>
              </details>

              <div className="rounded-md border border-slate-200 p-3">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Mode</div>
                <label className={`flex cursor-pointer items-start gap-2 rounded-md p-2 ${mode === "cross_reference" ? "bg-nmi-50 ring-1 ring-nmi-300" : "hover:bg-slate-50"}`}>
                  <input type="radio" name="cmode" className="mt-1" checked={mode === "cross_reference"} onChange={() => setMode("cross_reference")} />
                  <div>
                    <div className="text-sm font-bold text-slate-900">Cross-reference (safer)</div>
                    <div className="text-xs text-slate-600">Update matching, add new, preserve overrides. Missing rows are flagged or deleted (checkbox below).</div>
                  </div>
                </label>
                <label className={`mt-2 flex cursor-pointer items-start gap-2 rounded-md p-2 ${mode === "overwrite" ? "bg-red-50 ring-1 ring-red-300" : "hover:bg-slate-50"}`}>
                  <input type="radio" name="cmode" className="mt-1" checked={mode === "overwrite"} onChange={() => setMode("overwrite")} />
                  <div>
                    <div className="text-sm font-bold text-red-800">Ignore all &amp; overwrite (destructive)</div>
                    <div className="text-xs text-slate-600">Wipes the companies collection and inserts only rows from the file. Overrides are lost.</div>
                  </div>
                </label>
                {mode === "cross_reference" && (
                  <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={deleteMissing} onChange={(e) => setDeleteMissing(e.target.checked)} className="mt-0.5" />
                    <span><b>Hard-delete</b> companies missing from the new file ({preview.counts.removes}). Otherwise they're flagged.</span>
                  </label>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => { setConfirmOpen(true); setConfirmPhrase(""); }}
                  data-testid="companies-mass-replace-execute"
                  className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700"
                >
                  <Trash2 size={14} strokeWidth={2.5} /> {mode === "overwrite" ? "Wipe & replace" : "Apply changes"}
                </button>
              </div>
            </div>
          )}
        </div>

        {confirmOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 p-4" onClick={() => !executing && setConfirmOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border-2 border-red-300 bg-white p-5">
              <div className="mb-2 font-heading text-lg font-bold">Confirm mass replacement</div>
              <div className="text-xs text-slate-600 mb-3">
                Type <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono font-bold text-red-800">RESET</span> to confirm.
              </div>
              <input
                autoFocus
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                data-testid="companies-mass-replace-confirm"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:border-red-500 focus:outline-none"
                placeholder="Type RESET"
              />
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => setConfirmOpen(false)} disabled={executing} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
                <button
                  onClick={runReplace}
                  disabled={confirmPhrase !== "RESET" || executing}
                  className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {executing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={2.5} />}
                  Execute
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PreviewStat = ({ label, value, tone = "default" }) => {
  const tones = {
    default: "border-slate-200 bg-white",
    good: "border-emerald-200 bg-emerald-50",
    warn: "border-amber-200 bg-amber-50",
    info: "border-nmi-200 bg-nmi-50",
  };
  return (
    <div className={`rounded-md border ${tones[tone]} p-3`}>
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="mt-0.5 font-heading text-xl font-bold text-slate-900">{value.toLocaleString()}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Companies list page — with comprehensive filtering
// ---------------------------------------------------------------------------
export default function Companies() {
  const user = auth.me();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [showReplace, setShowReplace] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [meta, setMeta] = useState({ plans: [], statuses: [], sales_officers: [], relationship_officers: [] });

  // Filters
  const [fStatus, setFStatus] = useState("");
  const [fPlan, setFPlan] = useState("");
  const [fSalesOfficer, setFSalesOfficer] = useState("");
  const [fRelOfficer, setFRelOfficer] = useState("");
  const [fNature, setFNature] = useState("");
  const [fMinMembers, setFMinMembers] = useState("");
  const [fMaxMembers, setFMaxMembers] = useState("");
  const [fHasEmail, setFHasEmail] = useState(""); // '' | 'y' | 'n'
  const [fHasPhone, setFHasPhone] = useState("");

  // Sort
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");

  const pageSize = 25;

  useEffect(() => {
    const t = setTimeout(() => setDq(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    companiesApi.meta().then(setMeta).catch(() => {});
  }, []);

  const activeFilterCount = useMemo(() => [
    fStatus, fPlan, fSalesOfficer, fRelOfficer, fNature,
    fMinMembers, fMaxMembers, fHasEmail, fHasPhone,
  ].filter(Boolean).length, [fStatus, fPlan, fSalesOfficer, fRelOfficer, fNature, fMinMembers, fMaxMembers, fHasEmail, fHasPhone]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        q: dq || undefined,
        page,
        page_size: pageSize,
        sort,
        order,
      };
      if (fStatus) params.status = fStatus;
      if (fPlan) params.plan = fPlan;
      if (fSalesOfficer) params.sales_officer = fSalesOfficer;
      if (fRelOfficer) params.relationship_officer = fRelOfficer;
      if (fNature) params.nature_of_business = fNature;
      if (fMinMembers !== "") params.min_members = Number(fMinMembers);
      if (fMaxMembers !== "") params.max_members = Number(fMaxMembers);
      if (fHasEmail === "y") params.has_email = true;
      if (fHasEmail === "n") params.has_email = false;
      if (fHasPhone === "y") params.has_telephone = true;
      if (fHasPhone === "n") params.has_telephone = false;
      const d = await companiesApi.list(params);
      setItems(d.items); setTotal(d.total);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    dq, page, sort, order, fStatus, fPlan, fSalesOfficer, fRelOfficer,
    fNature, fMinMembers, fMaxMembers, fHasEmail, fHasPhone,
  ]);

  const clearFilters = () => {
    setFStatus(""); setFPlan(""); setFSalesOfficer(""); setFRelOfficer("");
    setFNature(""); setFMinMembers(""); setFMaxMembers(""); setFHasEmail(""); setFHasPhone("");
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await companiesApi.upload(file);
      toast.success(`Imported ${r.inserted + r.updated} companies (${r.inserted} new, ${r.updated} updated)`);
      if (r.errors?.length) toast.error(`${r.errors.length} row errors — see server logs`);
      load();
      companiesApi.meta().then(setMeta).catch(() => {});
    } catch (e) { toast.error(getApiError(e, "Upload failed")); }
    finally { setUploading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies/template.xlsx`, { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "companies_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  const pages = Math.ceil(total / pageSize);
  const setSortField = (f) => {
    if (sort === f) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(f); setOrder("asc"); }
  };
  const SortIcon = ({ f }) => {
    if (sort !== f) return <ArrowUpDown size={10} className="inline text-slate-300" />;
    return order === "asc" ? <ArrowUp size={10} className="inline text-nmi-700" /> : <ArrowDown size={10} className="inline text-nmi-700" />;
  };

  return (
    <AppLayout>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">Members</div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 md:text-3xl">Companies</h1>
          <p className="mt-1 text-xs text-slate-600">
            {meta.total?.toLocaleString?.() || meta.total || 0} companies · {meta.members_sum?.toLocaleString?.() || 0} total members across the book
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowAdd(true)}
              data-testid="add-company-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-2 text-xs font-semibold text-white hover:bg-nmi-800"
            >
              <Plus size={13} strokeWidth={2.5} /> Add company
            </button>
            <button
              onClick={downloadTemplate}
              data-testid="companies-template-download"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download size={13} /> Template
            </button>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-2 text-xs font-semibold text-white hover:bg-nmi-800">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={2.5} />}
              <input
                type="file"
                data-testid="companies-upload"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
              />
              Upload Excel
            </label>
            {isSuperAdmin && (
              <button
                onClick={() => setShowReplace(true)}
                data-testid="companies-mass-replace-open"
                className="inline-flex items-center gap-1.5 rounded-md border border-red-500 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
              >
                <ShieldAlert size={13} strokeWidth={2.5} /> Mass replace
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="companies-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, ID, contact, email, phone…"
            className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-nmi-600"
          />
        </div>
        <button
          onClick={() => setShowFilters((s) => !s)}
          data-testid="companies-toggle-filters"
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold ${activeFilterCount > 0 ? "border-nmi-600 bg-nmi-50 text-nmi-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
        >
          <Filter size={13} /> Filters
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-nmi-700 px-1.5 text-[10px] font-bold text-white">{activeFilterCount}</span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button onClick={clearFilters} className="text-[11px] font-semibold text-slate-500 underline hover:text-red-700">
            Clear all
          </button>
        )}
        <div className="ml-auto text-xs font-medium text-slate-500">{loading ? "…" : `${total.toLocaleString()} shown`}</div>
      </div>

      {showFilters && (
        <div className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="companies-filter-panel">
          <FilterSelect label="Status" value={fStatus} onChange={setFStatus} options={meta.statuses || []} testId="filter-status" />
          <FilterSelect label="Plan" value={fPlan} onChange={setFPlan} options={meta.plans || []} testId="filter-plan" />
          <FilterSelect label="Sales officer" value={fSalesOfficer} onChange={setFSalesOfficer} options={meta.sales_officers || []} testId="filter-sales-officer" />
          <FilterSelect label="Relationship officer" value={fRelOfficer} onChange={setFRelOfficer} options={meta.relationship_officers || []} testId="filter-rel-officer" />
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Nature of business</div>
            <input
              value={fNature}
              onChange={(e) => setFNature(e.target.value)}
              placeholder="e.g. Mining"
              className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-nmi-600"
              data-testid="filter-nature"
            />
          </div>
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Members range</div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={fMinMembers}
                onChange={(e) => setFMinMembers(e.target.value)}
                placeholder="min"
                className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-nmi-600"
                data-testid="filter-min-members"
              />
              <span className="text-slate-400 text-xs">–</span>
              <input
                type="number"
                value={fMaxMembers}
                onChange={(e) => setFMaxMembers(e.target.value)}
                placeholder="max"
                className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-nmi-600"
                data-testid="filter-max-members"
              />
            </div>
          </div>
          <FilterSelect
            label="Has email"
            value={fHasEmail}
            onChange={setFHasEmail}
            options={[{ value: "y", label: "Yes" }, { value: "n", label: "No" }]}
            testId="filter-has-email"
            raw
          />
          <FilterSelect
            label="Has phone"
            value={fHasPhone}
            onChange={setFHasPhone}
            options={[{ value: "y", label: "Yes" }, { value: "n", label: "No" }]}
            testId="filter-has-phone"
            raw
          />
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="cursor-pointer px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500" onClick={() => setSortField("name")}>
                Company <SortIcon f="name" />
              </th>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Plans</th>
              <th className="cursor-pointer px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500" onClick={() => setSortField("total_members")}>
                Members <SortIcon f="total_members" />
              </th>
              <th className="cursor-pointer px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500" onClick={() => setSortField("status")}>
                Status <SortIcon f="status" />
              </th>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Sales officer</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Report</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr
                key={c.company_id}
                data-testid={`company-row-${c.company_id}`}
                onClick={() => setDrawerId(c.company_id)}
                className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
              >
                <td className="px-3 py-2">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <div className="font-mono text-[10px] text-slate-500">{c.company_id}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(c.plans || []).map((p) => {
                      const n = c.members_by_plan?.[p];
                      return (
                        <span key={p} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider text-slate-700">
                          {p}{n ? ` · ${n.toLocaleString()}` : ""}
                        </span>
                      );
                    })}
                    {(!c.plans || c.plans.length === 0) && <span className="text-[11px] italic text-slate-400">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs font-mono text-slate-700">{c.total_members?.toLocaleString?.() || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-700">{c.status || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-700">{c.sales_officer || "—"}</td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${c.company_id}/report.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={async (e) => {
                      // Fetch via cookie-authed blob and open — no signed
                      // "share link" involved; only signed-in users see the PDF.
                      e.preventDefault();
                      try {
                        const blob = await companiesApi.reportPdfBlob(c.company_id);
                        const url = URL.createObjectURL(blob);
                        window.open(url, "_blank", "noopener,noreferrer");
                        setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      } catch { toast.error("Could not open report"); }
                    }}
                    data-testid={`company-row-report-${c.company_id}`}
                    className="inline-flex items-center gap-1 rounded border border-nmi-200 bg-nmi-50 px-1.5 py-0.5 text-[10px] font-semibold text-nmi-800 hover:bg-nmi-100"
                    title="Open PDF report"
                  >
                    <FileText size={10} strokeWidth={2.5} /> PDF
                  </a>
                </td>
                <td className="px-3 py-2"><ChevronRight size={14} className="text-slate-400" /></td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-xs text-slate-500">
                No companies match these filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-1.5 text-xs">
          {Array.from({ length: pages }).slice(0, 10).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`rounded-md border px-2.5 py-1 font-mono ${page === i + 1 ? "border-nmi-700 bg-nmi-700 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {drawerId && (
        <CompanyDrawer
          companyId={drawerId}
          onClose={() => setDrawerId(null)}
          onChanged={load}
        />
      )}

      {showReplace && (
        <MassReplaceModal
          onClose={() => setShowReplace(false)}
          onDone={() => { load(); companiesApi.meta().then(setMeta).catch(() => {}); }}
        />
      )}

      {showAdd && (
        <AddCompanyModal
          meta={meta}
          onClose={() => setShowAdd(false)}
          onCreated={(c) => {
            setShowAdd(false);
            toast.success(`${c.name} added`);
            load();
            companiesApi.meta().then(setMeta).catch(() => {});
            setDrawerId(c.company_id);
          }}
        />
      )}
    </AppLayout>
  );
}

const FilterSelect = ({ label, value, onChange, options, testId, raw = false }) => {
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        style={{ colorScheme: "light" }}
        className={`w-full rounded-md border bg-white px-2 py-1.5 text-xs outline-none focus:border-nmi-600 ${value ? "border-nmi-600 bg-nmi-50 text-nmi-900 font-semibold" : "border-slate-200 text-slate-700"}`}
      >
        <option value="">All</option>
        {options.map((o) => {
          const val = raw ? o.value : o;
          const lbl = raw ? o.label : o;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    </div>
  );
};

// ---------- Add Company (from scratch) modal ----------
const AddCompanyModal = ({ meta, onClose, onCreated }) => {
  const knownPlans = (meta?.plans && meta.plans.length ? meta.plans : ["PRESTIGE","PREMIER","EXECUTIVE","STANDARD","CLASSIC","BASIC","ESSENTIAL"]);
  const [form, setForm] = useState({
    company_id: "",
    name: "",
    total_members: "",
    start_date: "",
    end_date: "",
    join_date: "",
    status: "ACTIVE",
    sales_officer: "",
    relationship_officer: "",
    contact_person: "",
    telephone: "",
    email: "",
    postal_address: "",
    nature_of_business: "",
  });
  // Per-plan headcount ({PLAN: number}). A plan is considered active if its
  // count > 0.
  const [members, setMembers] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPlanCount = (p, v) => {
    const n = Number(v || 0);
    setMembers((m) => {
      const next = { ...m };
      if (Number.isFinite(n) && n > 0) next[p] = n; else delete next[p];
      return next;
    });
  };

  const submit = async () => {
    if (!form.company_id.trim() || !form.name.trim()) {
      toast.error("Company ID and name are required");
      return;
    }
    setBusy(true);
    try {
      const plansActive = Object.keys(members).filter((p) => members[p] > 0);
      const payload = {
        ...form,
        total_members: form.total_members === "" ? null : Number(form.total_members),
        plans: plansActive,
        members_by_plan: members,
      };
      const created = await companiesApi.create(payload);
      onCreated?.(created);
    } catch (e) {
      toast.error(getApiError(e, "Create failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => !busy && onClose()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl max-h-[90vh] flex flex-col"
        data-testid="add-company-modal"
      >
        <div className="flex items-start justify-between border-b border-slate-200 p-5">
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Add a new company</h3>
            <p className="mt-1 text-xs text-slate-500">
              Pick a company ID (must be unique), enter the plan headcount, and any contact details you have. Overrides can be added later from the drawer.
            </p>
          </div>
          <button onClick={onClose} disabled={busy} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <TextField label="Company ID" required value={form.company_id} onChange={(v) => set("company_id", v)} placeholder="e.g. C-0125" busy={busy} testId="add-company-id" />
            <TextField label="Company name" required value={form.name} onChange={(v) => set("name", v)} placeholder="e.g. Acme Corp Ltd" busy={busy} testId="add-company-name" />
            <TextField label="Total members" type="number" value={form.total_members} onChange={(v) => set("total_members", v)} placeholder="e.g. 350" busy={busy} />
            <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={meta?.statuses?.length ? meta.statuses : ["ACTIVE","DORMANT","TERMINATED"]} busy={busy} />
            <TextField label="Join date" value={form.join_date} onChange={(v) => set("join_date", v)} placeholder="YYYY-MM-DD" busy={busy} />
            <TextField label="Start date" value={form.start_date} onChange={(v) => set("start_date", v)} placeholder="YYYY-MM-DD" busy={busy} />
            <TextField label="End date" value={form.end_date} onChange={(v) => set("end_date", v)} placeholder="YYYY-MM-DD" busy={busy} />
            <TextField label="Nature of business" value={form.nature_of_business} onChange={(v) => set("nature_of_business", v)} placeholder="e.g. Mining" busy={busy} />
            <TextField label="Sales officer" value={form.sales_officer} onChange={(v) => set("sales_officer", v)} busy={busy} />
            <TextField label="Relationship officer" value={form.relationship_officer} onChange={(v) => set("relationship_officer", v)} busy={busy} />
            <TextField label="Contact person" value={form.contact_person} onChange={(v) => set("contact_person", v)} busy={busy} />
            <TextField label="Telephone" value={form.telephone} onChange={(v) => set("telephone", v)} busy={busy} />
            <TextField label="Email" value={form.email} onChange={(v) => set("email", v)} className="md:col-span-2" busy={busy} />
            <TextField label="Postal address" value={form.postal_address} onChange={(v) => set("postal_address", v)} className="md:col-span-2" busy={busy} />
          </div>

          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Members per plan</div>
            <p className="mb-2 text-xs text-slate-500">Any plan with a headcount &gt; 0 will be marked as active for this company.</p>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {knownPlans.map((p) => (
                <div key={p} className="rounded-md border border-slate-200 bg-white p-2 text-xs">
                  <div className="font-mono font-bold uppercase tracking-wider text-nmi-800">{p}</div>
                  <input
                    type="number"
                    min={0}
                    value={members[p] ?? ""}
                    onChange={(e) => setPlanCount(p, e.target.value)}
                    disabled={busy}
                    placeholder="0"
                    className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-sm outline-none focus:border-nmi-600"
                    data-testid={`add-company-plan-${p}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="add-company-cancel"
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !form.company_id.trim() || !form.name.trim()}
            data-testid="add-company-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-nmi-800 disabled:opacity-40"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} strokeWidth={2.5} />}
            Create company
          </button>
        </div>
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder, required, type = "text", className = "", busy, testId }) => (
  <div className={className}>
    <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
      {label}{required && <span className="text-red-500"> *</span>}
    </div>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      disabled={busy}
      data-testid={testId}
      className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-nmi-600"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, busy }) => (
  <div>
    <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={busy}
      style={{ colorScheme: "light" }}
      className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-nmi-600"
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

